import React from "react";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import {
  fetchNavbarAndFooter,
  fetchPaywallByIdServer,
} from "lib/fetchers/common";
import {
  BriefingReportPageDataResponse,
  fetchBriefingReportPageData,
} from "content-ui/briefings/fetchers/briefingReportFetcher";
import { TrackingData } from "lib/types/common";
import { getISODateFormatted } from "lib/utils";

import BriefingReportView from "content-ui/briefings/briefingReportView";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import GoogleAnalyticsBriefingsData from "components/thirdPartyScripts/googleAnalyticsBriefingsData";
import { makeDecisionServer } from "lib/zephr/public-api";
import { DecisionAction } from "lib/zephr/types";
import { PaywallWindow } from "lib/types/paywalls";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";
import { paywallBriefings } from "content-ui/briefings/paywallBriefings";
import { set } from "lodash";

export async function getServerSideProps({
  req,
  res,
  params,
  preview = false,
  previewData = null,
}) {
  const dato = await fetchBriefingReportPageData({
    slug: params.slug[0],
    preview,
  });
  if (!dato || !dato.briefing) return { notFound: true };

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  const blaizeSession = req.cookies["blaize_session"] || "";
  const decision = await makeDecisionServer(
    blaizeSession,
    "briefings-sdk",
    {
      path: req?.url,
    },
    req
  );
  const [menuItems, paywall] = await Promise.all([
    fetchNavbarAndFooter(),
    decision?.action === DecisionAction.DISPLAY_PAYWALL
      ? fetchPaywallByIdServer(decision?.payload?.paywallId)
      : null,
  ]);

  if (decision?.action !== DecisionAction.NONE) {
    set(dato, "briefing", paywallBriefings(dato.briefing));
  }

  return {
    props: {
      paywall,
      dato,
      trackingData: {
        mixpanel: {
          "Published date": getISODateFormatted(
            dato.briefing._firstPublishedAt
          ),
        },
      },
      slug: params.slug[0],
      canonical: buildCanonicalUrl(`/pro/briefings/${params.slug[0]}`),
      previewMode: preview,
      previewData,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type PageProps = {
  dato: BriefingReportPageDataResponse;
  previewMode: boolean;
  paywall: PaywallWindow | null;
  slug: string;
  canonical: string;
  trackingData: TrackingData;
};

const BriefingReportPage = (props: PageProps) => {
  const title = props?.dato?.briefing.name;
  const description = props?.dato?.briefing.description;
  const seoImage = props?.dato?.briefing.briefingImage?.url;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: props.canonical,
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <GoogleAnalyticsBriefingsData data={props.dato.briefing} />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/pro/briefings/${props.slug}`} />
      ) : null}
      <ErrorBoundary>
        <>
          {props.paywall !== null && (
            <ClientOnlyPaywall paywall={props.paywall} />
          )}
          <BriefingReportView
            blur={props.paywall !== null}
            briefing={props.dato.briefing}
          />
        </>
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default BriefingReportPage;
